// Global
import { classnames } from 'tailwindcss-classnames';
import { Components } from '.generated/templates/Feature.Components.model';
// Lib
import getThemeClasses, { ThemeOptions, ThemeOptionsDefault } from 'lib/util/theming';
import { getEnumValue } from 'lib/util/enum';
// Components

import PlaceholderWrapper from 'components/helpers/PlaceholderWrapper/PlaceholderWrapper';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import Container from 'components/helpers/Container';
import { PageSectionThemeProvider } from 'components/globalContext/PageSectionThemeContext';

export type LayoutPageSectionProps = Sitecore.Override.ComponentBase &
  Components.Layout.Fields.PageSection;

export type SectionPaddingOptions = 'tiny' | 'small' | 'normal';

export type LayoutPageSectionShellProps = {
  theme: ThemeOptions;
  children: React.ReactNode;
  padding?: SectionPaddingOptions;
  showTopRightDecoration?: boolean;
  showBottomLeftDecoration?: boolean;
  dataComponent?: string;
  overflow?: boolean;
  borderRadius?: boolean;
};

export const LayoutPageSectionShell = ({
  theme,
  children,
  padding,
  showTopRightDecoration,
  showBottomLeftDecoration,
  dataComponent,
  overflow,
  borderRadius,
}: LayoutPageSectionShellProps): JSX.Element => {
  // Maybe update this to add different HTML elements (<section> or <div>)
  // but then it also needs an optional hidden heading element.

  const circleBase = classnames(
    'block',
    'h-32',
    'w-32',
    'md:h-64',
    'md:w-64',
    'bg-green',
    'absolute',
    'bg-theme-circle-bg'
  );

  const topRightDecoration = classnames('rounded-bl-full', 'right-0', 'top-0');
  const bottomLeftDecoration = classnames('rounded-tr-full', 'left-0', 'bottom-0');

  const paddingClasses = classnames('md:px-20', {
    'md:py-6': padding === 'tiny',
    'md:py-10': padding === 'small',
    'md:py-12': padding === undefined || padding === 'normal',
  });

  const rounded = classnames(borderRadius ? 'lg:rounded-[30px]' : null);

  return (
    <Container
      size="standard"
      className={classnames(
        'block',
        'py-6',
        'px-4',
        paddingClasses,
        'px-gutter-all',
        { 'overflow-hidden': !overflow },
        'relative',
        'page-section',
        getThemeClasses(theme),
        rounded
      )}
      // `data-theme` is used here for the rare cases when we need to distinguish
      // between a theme class on the page versus a section.
      // See: app/src/components/authorable/promo/Banner/Banner.styles.css for an example.
      data-theme="page-section"
      data-current-theme={theme}
      dataComponent={dataComponent}
    >
      {showTopRightDecoration ? (
        <div className={classnames(circleBase, topRightDecoration)} />
      ) : null}

      {showBottomLeftDecoration ? (
        <div className={classnames(circleBase, bottomLeftDecoration)} />
      ) : null}

      <div className={classnames('relative', { 'z-0': !overflow, 'z-30': overflow })}>
        <PageSectionThemeProvider theme={theme}>{children}</PageSectionThemeProvider>
      </div>
    </Container>
  );
};

const LayoutPageSection = ({ fields, rendering }: LayoutPageSectionProps): JSX.Element => {
  // Maybe update this to add different HTML elements (<section> or <div>)
  // but then it also needs an optional hidden heading element.

  const sectionTheme =
    getEnumValue<ThemeOptions>(fields?.sectionBackgroundColor) || ThemeOptionsDefault;

  return (
    <LayoutPageSectionShell
      theme={sectionTheme}
      showBottomLeftDecoration={fields?.bottomLeftDecoration?.value ?? false}
      showTopRightDecoration={fields?.topRightDecoration?.value ?? false}
      dataComponent="authorable/layout/layout-page-section"
      borderRadius={fields?.borderRadius?.value ?? false}
    >
      <PlaceholderWrapper name="nmdp-full-width" rendering={rendering} isSingleColumn />
    </LayoutPageSectionShell>
  );
};

export default LayoutPageSection;
